<template>
    <div>
        <AsCoachInspectWorkspace
            :inspect="inspectCtx"
            :breadcrumbs="inspectChecklistBreadcrumbs"
            @breadcrumb-click="handleInspectChecklistBreadcrumbClick"
        />

        <transition name="fade">
            <GuideVideoBanner
                v-if="!showInspectChecklistView && !isOrganizationInspectionOrigin && guideData && showGuideCard"
                :text="guideData.content.text"
                :subtext="guideData.content.subText"
                :videoUrl="guideData.content.videoUrl"
                :videoThumbnail="guideData.content.videoThumbnail"
                @close="handleGuideClose"
            />
        </transition>
        <ExGenericBanner
            v-if="!showInspectChecklistView && !isOrganizationInspectionOrigin && bannerData.title"
            :title="bannerData.title"
            :description="bannerData.description"
            :buttonText="bannerData.buttonText"
            :icon="bannerData.icon"
            style="margin: 14px 0px;"
        />
        <div
            v-if="!showInspectChecklistView && !isOrganizationInspectionOrigin"
            class="your-employees-to-inspect-tab-wrapper mt-2"
            :class="{ 'has-data': hasAnyData }"
        >
            <SharedTableSkeleton
                v-if="showSkeletonLoading"
                :row-count="perPage || 5"
                :column-count="4"
                column-template="2fr 2fr 200px 160px"
            />
            <NoRecordFound
                v-else-if="!hasAnyData"
                title="Employees"
                :should-show-add-action="false"
                description="There are no Employees with Assigned Responsibilities that you are a coach of"
                class="no-record-found"
                :noBorder="true"
                :showActiveCounter="false"
                showSpecificMessage="You currently have no Employees to Inspect"
                :showClearAction="false"
                message="Employees"
                btnTitle="Employee"
                @onClear="() => {}"
            />
            <YourEmployeesToInspectTable 
                v-else
                :employeesData="employees" 
                :showPagination="showPagination" 
                :showNoRecords="employees.length === 0 && searchQuery === ''"
                :pagination="{ total: totalCount, page: currentPage, perPage: perPage }"
                :filterText="searchQuery"
                :chipText="chipText"
                :defaultSelected="badgeFilters"
                :levelCounter="levelCounter"
                @onSearch="handleSearch"
                @onMultipleFieldSearch="handleMultipleFieldSearch"
                @onPaginationRequest="handlePaginationRequest"
                @onIssueBadgeClick="handleIssueBadgeClick($event)"
                @onBadgeRemoved="handleBadgeRemoved"
                @expiration-synced="handleCoachExpirationSynced"
                @checkedItemsChanged="handleCheckedItemsChanged"
                @removeFilter="handleRemoveFilter"
                @clearSelection="handleClearSelection"
                @onLevelsChange="handleLevelsChange"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, getCurrentInstance, nextTick } from 'vue';
import YourEmployeesToInspectTable from '@components/YourEmployeesToInspectTable/YourEmployeesToInspectTable.vue';
import NoRecordFound from '@components/NoRecordFound/NoRecordFound.vue';
import AsCoachInspectWorkspace from '@components/AsCoachInspectWorkspace/AsCoachInspectWorkspace.vue';
import { useCoachInspect } from '@components/AsCoachInspectWorkspace/composables/useCoachInspect';
import { buildInspectionRowFromOrganizationContext } from '@components/utils/buildInspectionRowFromOrganizationContext';
import {
    getEmployeesToInspect,
    getEmployeeCoachCounts,
    getEmployeeBadges,
    getEmployeeInspectionData,
} from '@/api';
import { useFiltersStore } from '@/store/pinia/filters';
import { getDefaultFiltersForKey } from '@components/constants/filterDefaultsRegistry';
import ExGenericBanner from '@components/GenericBanner/ExGenericBanner.vue';
import GuideVideoBanner from '@components/GuideVideoBanner/GuideVideoBanner.vue';
import SharedTableSkeleton from '@components/SharedTableSkeleton/SharedTableSkeleton.vue';
import { LD_FLAG_IDEA_MDP_560 } from '@components/constants/launchDarklyFlagKeys';
import { useKnockGuides } from "@/composables/common/useKnockGuides";

const props = defineProps({
    mockGuideData: {
        type: Object,
        default: null,
    },
    mockData: {
        type: Array,
        default: null,
    },
});

const { proxy } = getCurrentInstance();
const filtersStore = useFiltersStore();

const isOrganizationInspectionOrigin = computed(() => {
    const route = proxy.$route || {};
    if (route.name === 'OrganizationEmployeeInspection' || route.query?.from === 'organization') {
        return true;
    }
    return typeof route.path === 'string' && /\/organization\/employees\/[^/]+\/inspection\/?$/.test(route.path);
});

function getInspectionEmployeeId() {
    return proxy.$route?.params?.id || proxy.$route?.query?.employeeId;
}

function getOrganizationEmployeeReturnPath() {
    const employeeId = getInspectionEmployeeId();
    return employeeId
        ? `/organization/employees/${employeeId}/pathtoreadiness`
        : '/organization/employees';
}

const ORGANIZATION_INSPECTION_CONTEXT_KEY = 'exiqtive.organizationInspectionContext';

function readOrganizationInspectionContext() {
    try {
        const raw = sessionStorage.getItem(ORGANIZATION_INSPECTION_CONTEXT_KEY);
        if (!raw) return null;
        sessionStorage.removeItem(ORGANIZATION_INSPECTION_CONTEXT_KEY);
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
}

// Filter key for localStorage persistence
const BADGE_FILTERS_KEY = 'inspectionBadgeFilters';

const employees = ref([]);
const perPage = ref(10);
const totalCount = ref(0);
const currentPage = ref(1);
const showPagination = ref(false);
const searchQuery = ref("");
/** When using multi-field search: { employee_name?, responsibility_name? }. Null when using simple search. */
const searchFilters = ref(null);
const badgeFilters = ref(getDefaultFiltersForKey(BADGE_FILTERS_KEY) || []);
let searchTimeout = null;
const bannerData = ref({
    title: null,
    description: null,
    buttonText: null,
    icon: null,
});

// Drop deep-link query params so returning to the list does not reopen the assignment.
// ER-11639: Organization origin returns to the employee Path to Readiness page.
function handleInspectViewClosed() {
    if (isOrganizationInspectionOrigin.value) {
        proxy.$router?.push(getOrganizationEmployeeReturnPath());
        return;
    }
    const currentQuery = proxy.$route?.query || {};
    if (currentQuery.employeeId || currentQuery.responsibilityId) {
        proxy.$router?.replace({ path: proxy.$route.path });
    }
}

const coachInspect = useCoachInspect({
    onListRefresh: () => fetchEmployeesToInspect(),
    onViewClosed: handleInspectViewClosed,
});
const inspectCtx = reactive({ ...coachInspect });
const {
    showInspectChecklistView,
    coachChecklistItems,
    coachChecklistPassFilter,
    coachChecklistExpandedLevel,
    showCoachAwardBadgeDialog,
    coachAwardBadgeLevel,
    showEmployeeModal,
    showUpgradeBadgeDialog,
    selectedEmployee,
    selectedResponsibility,
    selectedRowData,
    currentAction,
    giveBadgeFromInspection,
    inspectChecklistResponsibility,
    extractCodeNumber,
    selectInspectionTarget,
    openInspection,
    fetchCoachChecklist,
    onCoachChecklistPassFilterChange,
    handleCoachChecklistBadgeClick,
    handleCoachAwardBadgeActivate,
    closeInspectChecklistView,
    handleGiveBadge,
    handleBadgeChatter,
    closeChatterDrawer,
    loadMoreChatter,
    showChatterDrawer,
    chatterData,
    totalChatter,
    findCurrentBadgeForResponsibility,
    buildCoachBadgeSliceFromApiRow,
} = coachInspect;


// ER-10597 / MDP-560 — table loading state drives the shared skeleton (before
// empty-state and before the real grid), mirroring OrganizationRolesTable.vue.
const loading = ref(false);
const skeletonEnabled = ref(false);

const showSkeletonLoading = computed(() => loading.value && skeletonEnabled.value);

async function checkSkeletonFlag() {
    try {
        skeletonEnabled.value =
            (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
    } catch {
        skeletonEnabled.value = false;
    }
}

const hasAnyData = computed(() => {
    if (searchQuery.value !== "") return true;
    if (
        searchFilters.value &&
        (searchFilters.value.employee_name || searchFilters.value.responsibility_name)
    )
        return true;
    if (badgeFilters.value && badgeFilters.value.length > 0) return true;
    return totalCount.value > 0 || employees.value.length > 0;
});


// Computed property for chip text display
const chipText = computed(() => {
    return badgeFilters.value.map(filter => 
        filter.charAt(0).toUpperCase() + filter.slice(1)
    );
});


// Guide banner state
const guideData = ref(null);
const showGuideCard = ref(true);
const levelCounter = ref({});

const { selectGuide, subscribeToGuides } = useKnockGuides();
let unsubscribeGuide = null;

const refreshGuide = () => {
    const coachGuide = selectGuide({ key: "what-is-expected-of-you-as-a-coach" });
    // Use Knock guide data if available
    if (coachGuide?.steps?.[0]) {
        guideData.value = coachGuide.steps[0];
    }
};

const initializeKnock = () => {
    unsubscribeGuide = subscribeToGuides(refreshGuide);
    refreshGuide();
};

const handleGuideClose = async () => {
    showGuideCard.value = false;
    
    if (guideData.value && guideData.value.markAsArchived) {
        try {
            await guideData.value.markAsArchived();
        } catch (error) {
            console.error('Error archiving guide in Knock:', error);
        }
    }
};

onBeforeUnmount(() => {
    if (typeof unsubscribeGuide === "function") {
        unsubscribeGuide();
        unsubscribeGuide = null;
    }
});

onMounted(async () => {
    // ER-10597 / MDP-560 — when the consistent-skeleton flag is on, the table
    // renders its own skeleton during the initial fetch, so the global spinner is
    // only used for the organization-origin inspection flow (which never renders
    // the table) or when the flag is off (legacy behaviour).
    await checkSkeletonFlag();
    if (isOrganizationInspectionOrigin.value || !skeletonEnabled.value) {
        proxy.$loader.show();
    }
    try {
        filtersStore.loadFilters(BADGE_FILTERS_KEY);
        badgeFilters.value = (filtersStore.filters && filtersStore.filters.length > 0)
            ? filtersStore.filters
            : (getDefaultFiltersForKey(BADGE_FILTERS_KEY) || []);

        // Use mock guide data if provided (for Storybook), otherwise initialize Knock
        if (props.mockGuideData) {
            guideData.value = props.mockGuideData;
        } else {
            await initializeKnock();
        }

        // Use mock data if provided (for Storybook), otherwise fetch from API.
        // ER-11639: Organization-origin Inspect must not load the coach queue.
        if (!isOrganizationInspectionOrigin.value) {
            if (props.mockData) {
                employees.value = props.mockData;
                totalCount.value = props.mockData.length;
                showPagination.value = props.mockData.length > perPage.value;
                if (props.mockData.length > 0) {
                    bannerData.value = {
                        title: `You currently have ${props.mockData.length} potential ${props.mockData.length === 1 ? 'responsibility' : 'responsibilities'} to inspect`,
                        description: 'When employees are ready for inspection, they should reach out to you to schedule it.',
                        buttonText: null,
                        icon: 'info_icon',
                    };
                }
            } else {
                await fetchEmployeesToInspect({ skipLoader: true });
            }
        }

        // Deep-link support (ER-11331): the badge-issuance flow routes here with
        // employeeId + responsibilityId query params so the coach inspection screen
        // opens directly on the selected assignment instead of the legacy modal.
        // ER-11639: Organization Inspect uses /organization/employees/:id/inspection
        // with employeeId in the path params instead of the Your Stuff query.
        const deepLinkEmployeeId = getInspectionEmployeeId();
        const deepLinkResponsibilityId = proxy.$route?.query?.responsibilityId;
        const organizationContext = isOrganizationInspectionOrigin.value
            ? readOrganizationInspectionContext()
            : null;

        if (organizationContext) {
            const row = buildInspectionRowFromOrganizationContext(organizationContext);
            if (row) {
                await handleIssueBadgeClick({ data: row, action: 'Inspect' });
            }
        } else if (deepLinkEmployeeId && deepLinkResponsibilityId) {
            await openDeepLinkedInspection(deepLinkEmployeeId, deepLinkResponsibilityId);
        } else if (isOrganizationInspectionOrigin.value) {
            proxy.$router?.replace(getOrganizationEmployeeReturnPath());
        }
    } finally {
        if (isOrganizationInspectionOrigin.value || !skeletonEnabled.value) {
            proxy.$loader.hide();
        }
    }
});


const inspectChecklistBreadcrumbs = computed(() => {
    if (isOrganizationInspectionOrigin.value) {
        return [
            { text: 'Organization', href: '/organization', disabled: false },
            { text: 'Employees', href: '/organization/employees', disabled: false },
            {
                text: proxy.$route?.query?.employeeName || selectedEmployee.value?.fullName || 'Employee',
                href: getOrganizationEmployeeReturnPath(),
                disabled: false,
                truncate: true,
            },
            { text: 'Inspection', disabled: true },
        ];
    }
    return [
        { text: 'Responsibilities to Inspect', href: '#', disabled: false },
        { text: 'Employee Inspection Checklist', disabled: true },
    ];
});


const fetchEmployeesToInspect = async ({ skipLoader = false } = {}) => {
    try {
        if (!skipLoader) proxy.$loader.show();
        loading.value = true;
        const accountID = localStorage.getItem("currentAccountID");

        const fetchLimit = perPage.value;
        const fetchOffset = (currentPage.value - 1) * perPage.value;

        let response = await getEmployeesToInspect(
            accountID,
            fetchLimit,
            fetchOffset,
            searchQuery.value,
            false,
            "name",
            badgeFilters.value,
            searchFilters.value
        );

        const countsResponse = await getEmployeeCoachCounts(accountID);
        const counts = countsResponse.data;
        levelCounter.value = {
            Apprentice: counts.apprentice,
            Professional: counts.professional,
            Coach: counts.coach,
            Master: counts.master,
            Novice: counts.novice,
        };

        if (response?.data) {
            const results = response.data.results || [];
            const transformedData = [];

            results.forEach(item => {
                if (item.first_name && item.last_name) {
                    const rawRespCode = item.responsibilityAssignments?.responsibility_code || '';
                    const cleanRespCode = extractCodeNumber(rawRespCode);
                    
                    const rawRoleCode = item.responsibilityAssignments?.role_code || '';
                    const cleanRoleCode = extractCodeNumber(rawRoleCode);
                    
                    const rawPositionCode = item.responsibilityAssignments?.position_code || '';
                    const cleanPositionCode = extractCodeNumber(rawPositionCode);
                    
                    transformedData.push({
                        id: item.id,
                        user: {
                            id: item.user || item.id,
                            name: `${item.first_name} ${item.last_name}`,
                            email: item.email || '',
                            avatar: item.avatar || null,
                            position: item.primary_position?.name || '',
                            department: '',
                        },
                        responsibilityAssignments: {
                            responsibility_id: item.responsibilityAssignments?.responsibility_id || '',
                            responsibility_code: cleanRespCode ? `#${cleanRespCode}` : rawRespCode,
                            responsibility_name: item.responsibilityAssignments?.responsibility_name || '',
                            responsibility_importance: item.responsibilityAssignments?.responsibility_importance || 3,
                            role_id: item.responsibilityAssignments?.role_id || '',
                            role_code: cleanRoleCode ? `#${cleanRoleCode}` : rawRoleCode,
                            role_name: item.responsibilityAssignments?.role_name || '',
                            role_importance: item.responsibilityAssignments?.role_importance || 3,
                            position_id: item.responsibilityAssignments?.position_id || '',
                            position_code: cleanPositionCode ? `#${cleanPositionCode}` : rawPositionCode,
                            position_name: item.responsibilityAssignments?.position_name || '',
                            position_importance: item.responsibilityAssignments?.position_importance || 3,
                            position_commitment: item.responsibilityAssignments?.position_commitment || 'Primary',
                            assigned_on: item.primary_position?.effective_on || null,
                            new_duration: 15,
                        },
                        badge: {
                            id: item.current_badge?.id || null,
                            level: (item.current_badge?.level || 'Novice').charAt(0).toUpperCase() + (item.current_badge?.level || 'Novice').slice(1)?.toLowerCase(),
                            status: item.current_badge?.status || 'No Dot',
                            checkpoints: item.current_badge?.checkpoints || [],
                            issued_on: item.current_badge?.issued_on ?? null,
                            issued_method: item.current_badge?.issued_method ?? null,
                            expiration_date: item.current_badge?.expiration_date ?? null,
                            expires_in_days_from_becoming_obsolete:
                                item.current_badge?.expires_in_days_from_becoming_obsolete ?? null,
                            has_expiration_if_obsolete: !!item.current_badge?.has_expiration_if_obsolete,
                            retries_to_expire_in_days:
                                item.current_badge?.retries_to_expire_in_days ?? undefined,
                        },
                        inspectionStatus: item.inspectionStatus || 'Pending',
                        lastInspection: item.lastInspection || null,
                        checkpointCounts: item.checkpointCounts || {
                            apprentice_checkpoint_count: 0,
                            apprentice_passed_checkpoint_count: 0,
                            professional_checkpoint_count: 0,
                            professional_passed_checkpoint_count: 0,
                            coach_checkpoint_count: 0,
                            coach_passed_checkpoint_count: 0,
                            master_checkpoint_count: 0,
                            master_passed_checkpoint_count: 0,
                        },
                        originalEmployee: item,
                    });
                }
            });

            const dataToShow = transformedData;
            const countToShow = response.data.count ?? transformedData.length;

            employees.value = dataToShow;
            totalCount.value = countToShow;
            showPagination.value = totalCount.value > perPage.value;

            await refreshCoachEmployeesBadgesFromApi();
            
            // Set banner data when employees are fetched
            if (totalCount.value > 0) {
                bannerData.value = {
                    title: `You currently have ${totalCount.value} potential ${totalCount.value === 1 ? 'responsibility' : 'responsibilities'} to inspect`,
                    description: 'When employees are ready for inspection, they should reach out to you to schedule it.',
                    buttonText: null,
                    icon: 'info_icon',
                };
            } else {
                bannerData.value = {
                    title: null,
                    description: null,
                    buttonText: null,
                    icon: null,
                };
            }
        }
    } catch (error) {
        console.error('Error fetching employees to inspect:', error.message);
        employees.value = [];
        totalCount.value = 0;
        showPagination.value = false;
        bannerData.value = {
            title: null,
            description: null,
            buttonText: null,
            icon: null,
        };
    } finally {
        loading.value = false;
        if (!skipLoader) proxy.$loader.hide();
    }
};

const handleIssueBadgeClick = async (event) => {
    const { data, action } = event;
    currentAction.value = action;

    if (action === 'Inspect') {
        await openInspection(data);
        return;
    }

    selectInspectionTarget(data);

    if (action === 'Give Upgrade') {
        if (showEmployeeModal.value) {
            showEmployeeModal.value = false;
            await nextTick();
        }
        showUpgradeBadgeDialog.value = true;
        return;
    }

    if (showEmployeeModal.value) {
        showEmployeeModal.value = false;
        await nextTick();
    }
    showEmployeeModal.value = true;
};

/**
 * Deep-link entry (ER-11331): open the inspect checklist for a specific
 * employee + responsibility passed via route query (from the badge-issuance
 * flow). Loads the same employee/inspection context the employees-to-inspect
 * list would provide, then reuses the existing Inspect flow.
 */
async function openDeepLinkedInspection(employeeId, responsibilityId) {
    const accountId = localStorage.getItem('currentAccountID');
    if (!accountId || !employeeId || !responsibilityId) return;
    try {
        const { data } = await getEmployeeInspectionData(accountId, employeeId, responsibilityId);
        if (!data) {
            if (isOrganizationInspectionOrigin.value) {
                proxy.$router?.replace(getOrganizationEmployeeReturnPath());
            }
            return;
        }
        await handleIssueBadgeClick({ data: buildInspectionRowFromEmployeeData(data, responsibilityId), action: 'Inspect' });
    } catch (error) {
        console.error('Failed to open deep-linked inspection:', error);
        if (isOrganizationInspectionOrigin.value) {
            proxy.$router?.replace(getOrganizationEmployeeReturnPath());
        }
    }
}

function buildInspectionRowFromEmployeeData(apiData, responsibilityId) {
    const employeeName = `${apiData.first_name || ''} ${apiData.last_name || ''}`.trim();
    const ra = apiData.responsibilityAssignments || {};
    const cleanRespCode = extractCodeNumber(ra.responsibility_code);
    const badgeSource = apiData.badge || apiData.current_badge || {};
    const badgeLevel = (badgeSource.level || 'Novice');
    const normalizedBadgeLevel =
        badgeLevel.charAt(0).toUpperCase() + badgeLevel.slice(1).toLowerCase();
    return {
        id: apiData.id,
        user: {
            id: apiData.id,
            name: employeeName,
            email: apiData.email || '',
            avatar: apiData.avatar || null,
            position: apiData.primary_position?.name || '',
            department: '',
        },
        responsibilityAssignments: {
            responsibility_id: ra.responsibility_id || responsibilityId || '',
            responsibility_code: cleanRespCode ? `#${cleanRespCode}` : ra.responsibility_code,
            responsibility_name: ra.responsibility_name || '',
            responsibility_importance: ra.responsibility_importance || 3,
            role_id: ra.role_id || '',
            role_code: ra.role_code || '',
            role_name: ra.role_name || '',
            role_importance: ra.role_importance || 3,
            position_id: ra.position_id || '',
            position_code: ra.position_code || '',
            position_name: ra.position_name || '',
            position_importance: ra.position_importance || 3,
            position_commitment: ra.position_commitment || 'Primary',
            assigned_on: ra.assigned_on || apiData.primary_position?.effective_on || null,
            new_duration: ra.new_duration || 15,
        },
        badge: {
            id: badgeSource.id || null,
            level: normalizedBadgeLevel,
            status: badgeSource.status || 'No Dot',
            checkpoints: badgeSource.checkpoints || [],
            issued_on: badgeSource.issued_on ?? null,
            issued_method: badgeSource.issued_method ?? null,
            expiration_date: badgeSource.expiration_date ?? null,
            expires_in_days_from_becoming_obsolete:
                badgeSource.expires_in_days_from_becoming_obsolete ?? null,
            has_expiration_if_obsolete: !!badgeSource.has_expiration_if_obsolete,
            retries_to_expire_in_days: badgeSource.retries_to_expire_in_days ?? undefined,
        },
        inspectionStatus: apiData.inspectionStatus || 'Pending',
        lastInspection: apiData.lastInspection || null,
        checkpointCounts: apiData.checkpointCounts || {
            apprentice_checkpoint_count: 0,
            apprentice_passed_checkpoint_count: 0,
            professional_checkpoint_count: 0,
            professional_passed_checkpoint_count: 0,
            coach_checkpoint_count: 0,
            coach_passed_checkpoint_count: 0,
            master_checkpoint_count: 0,
            master_passed_checkpoint_count: 0,
        },
        originalEmployee: apiData,
    };
}


function handleInspectChecklistBreadcrumbClick(breadcrumb) {
    if (breadcrumb?.disabled) return;
    // Organization crumbs already navigate via href in ExBreadcrumbs. Do not
    // close back onto the coach queue — that would wipe the Organization route.
    if (isOrganizationInspectionOrigin.value && breadcrumb?.href) {
        return;
    }
    closeInspectChecklistView();
}


/**
 * Proficiency `.../employee/{emp_id}/badge/` expects account employee id — same as
 * `selectedEmployee.id` (`employee_id || id` from list payload), not auth `user.id`.
 */
function getProficiencyEmployeeIdForCoachRow(row) {
    const o = row?.originalEmployee;
    if (o && typeof o === 'object') {
        const id = o.employee_id ?? o.employee;
        if (id != null && id !== '') return id;
        if (o.id != null && o.id !== '') return o.id;
    }
    const fallback = row?.user?.id;
    return fallback != null && fallback !== '' ? fallback : null;
}

function coachRowResponsibilityIdForBadgeMatch(row) {
    let resId = row?.responsibilityAssignments?.responsibility_id;
    if (resId === '' || resId == null) {
        resId =
            row?.originalEmployee?.responsibilityAssignments?.responsibility_id ??
            row?.originalEmployee?.responsibility_id ??
            null;
    }
    return resId === '' ? null : resId;
}

/**
 * After list load or Pinia `expirationSynced`, merge each row's badge from `getEmployeeBadges`
 * so expiration / status match the proficiency API (employee list alone may omit fields).
 */
async function refreshCoachEmployeesBadgesFromApi() {
    const accountId = localStorage.getItem('currentAccountID');
    const rows = employees.value;
    if (!accountId || !Array.isArray(rows) || rows.length === 0) return;

    const byEmployeeId = new Map();
    for (const row of rows) {
        const empId = getProficiencyEmployeeIdForCoachRow(row);
        if (empId == null || empId === '') continue;
        const key = String(empId);
        if (!byEmployeeId.has(key)) byEmployeeId.set(key, { empId, rows: [] });
        byEmployeeId.get(key).rows.push(row);
    }

    for (const { empId, rows: groupRows } of byEmployeeId.values()) {
        try {
            const badgesRes = await getEmployeeBadges(accountId, empId);
            const results = badgesRes?.data?.results || [];
            for (const row of groupRows) {
                const resId = coachRowResponsibilityIdForBadgeMatch(row);
                const currentBadge = findCurrentBadgeForResponsibility(results, resId, row);
                const slice = buildCoachBadgeSliceFromApiRow(currentBadge);
                if (!slice) continue;
                if (row.badge && typeof row.badge === 'object') {
                    Object.assign(row.badge, slice);
                } else {
                    row.badge = slice;
                }
            }
        } catch (e) {
            console.warn('refreshCoachEmployeesBadgesFromApi:', empId, e);
        }
    }

    // New array + badge objects so Vue / DevExtreme see updated badge column (in-place assign alone can skip refresh).
    employees.value = employees.value.map((row) => ({
        ...row,
        badge: row.badge ? { ...row.badge } : row.badge,
    }));
}

/** Refetch coach list; `fetchEmployeesToInspect` merges rows via `getEmployeeBadges` (real-time badge/expiration). */
const handleCoachExpirationSynced = async () => {
    await fetchEmployeesToInspect();
};


const handleBadgeRemoved = async () => {
    await fetchEmployeesToInspect();
};

const handleSearch = (searchText) => {
    searchQuery.value = searchText || "";
    searchFilters.value = null;

    if (searchTimeout) {
        clearTimeout(searchTimeout);
    }

    // If clearing search, fetch immediately without delay
    const delay = searchText === "" ? 0 : 500;

    searchTimeout = setTimeout(async () => {
        currentPage.value = 1;
        await fetchEmployeesToInspect();
    }, delay);
};

/** Apply multi-field filters (employee name, responsibility name) and refetch. */
const handleMultipleFieldSearch = (filters) => {
    if (!filters || filters.length === 0) {
        searchFilters.value = null;
        searchQuery.value = "";
    } else {
        const byKey = {};
        filters.forEach((f) => {
            const v = (f.value || "").trim();
            if (v) byKey[f.fieldKey] = v;
        });
        searchFilters.value = {
            employee_name: byKey.employee_name || "",
            responsibility_name: byKey.responsibility_name || "",
        };
        searchQuery.value = filters.map((f) => (f.value || "").trim()).filter(Boolean).join(" ");
    }
    currentPage.value = 1;
    fetchEmployeesToInspect();
};

const handlePaginationRequest = async (paginationData) => {
    currentPage.value = paginationData.page;
    await fetchEmployeesToInspect();
};

const handleCheckedItemsChanged = (checkedItems) => {
    const readinessFilters = checkedItems
        .filter(item => {
            const value = typeof item === 'string' ? item : item.value;
            return ['novice', 'apprentice', 'professional'].includes(value?.toLowerCase());
        })
        .map(item => typeof item === 'string' ? item : item.value);
    
    badgeFilters.value = readinessFilters;

    filtersStore.setFilters({ 
        key: BADGE_FILTERS_KEY, 
        filters: badgeFilters.value 
    });
    
    currentPage.value = 1;
    fetchEmployeesToInspect();
};

const handleRemoveFilter = (removedFilter) => {
    const filterValue = removedFilter?.toLowerCase();
    badgeFilters.value = badgeFilters.value.filter(f => f.toLowerCase() !== filterValue);
    filtersStore.setFilters({ 
        key: BADGE_FILTERS_KEY, 
        filters: badgeFilters.value 
    });
    
    currentPage.value = 1;
    fetchEmployeesToInspect();
};

const handleClearSelection = (removedFilter) => {
    if (removedFilter) return;

    badgeFilters.value = [];
    filtersStore.setFilters({ 
        key: BADGE_FILTERS_KEY, 
        filters: [] 
    });
    currentPage.value = 1;
    fetchEmployeesToInspect();
};

const handleLevelsChange = (levels) => {
    badgeFilters.value = levels;
    filtersStore.setFilters({
        key: BADGE_FILTERS_KEY,
        filters: badgeFilters.value
    });
    currentPage.value = 1;
    fetchEmployeesToInspect();
};


defineExpose({
    employees,
    fetchEmployeesToInspect,
    totalCount,
    currentPage,
    showPagination,
    perPage,
    badgeFilters,
    bannerData,
    handleCoachAwardBadgeActivate,
    handleCoachChecklistBadgeClick,
    handleGiveBadge,
    selectedEmployee,
    selectedResponsibility,
    coachAwardBadgeLevel,
    coachChecklistItems,
    giveBadgeFromInspection,
    showInspectChecklistView,
    showCoachAwardBadgeDialog,
    selectedRowData,
    fetchCoachChecklist,
    closeInspectChecklistView,
    inspectChecklistBreadcrumbs,
    inspectChecklistResponsibility,
    isOrganizationInspectionOrigin,
    handleInspectChecklistBreadcrumbClick,
    onCoachChecklistPassFilterChange,
    coachChecklistPassFilter,
    coachChecklistExpandedLevel,
    handleBadgeChatter,
    closeChatterDrawer,
    loadMoreChatter,
    showChatterDrawer,
    chatterData,
    totalChatter,
});
</script>

<style scoped>
.your-employees-to-inspect-tab-wrapper {
    border-radius: 8px;
    border: 1px solid #EAECF0;
    background: #FFF;
    box-shadow: 0 1px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06);
}

.your-employees-to-inspect-tab-wrapper.has-data {
    padding-top: 22px;
}

::v-deep(.input-search) {
    left: -12px !important;
}

/* Fade transition styles */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
</style>